/**
 * Context Menu Component
 * Provides right-click menu for diagram elements
 * Features: Group, Ungroup, Lock, Unlock, Delete, Duplicate, Auto-resize toggle
 */

import { memo, useEffect, useCallback } from 'react'
import { Node } from '@xyflow/react'
import {
  Group, Ungroup, Lock, Unlock, Trash2, Copy,
  Clipboard, Maximize2, Minimize2,
  AlignHorizontalJustifyStart, AlignVerticalJustifyStart,
  AlignHorizontalDistributeCenter, LayoutGrid,
  BringToFront, SendToBack, ArrowUp, ArrowDown
} from 'lucide-react'
import '../styles/context-menu.css'

export interface ContextMenuItem {
  label: string
  icon?: React.ReactNode
  onClick: () => void
  disabled?: boolean
  divider?: boolean
  destructive?: boolean
  shortcut?: string
}

export interface ContextMenuProps {
  position: { x: number; y: number }
  items: ContextMenuItem[]
  onClose: () => void
}

function ContextMenu({ position, items, onClose }: ContextMenuProps) {
  // Close on click outside
  useEffect(() => {
    const handleClickOutside = () => onClose()
    // Small delay to prevent immediate closing from the right-click that opened it
    const timer = setTimeout(() => {
      document.addEventListener('click', handleClickOutside)
    }, 100)

    return () => {
      clearTimeout(timer)
      document.removeEventListener('click', handleClickOutside)
    }
  }, [onClose])

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [onClose])

  const handleItemClick = useCallback((item: ContextMenuItem) => {
    if (!item.disabled) {
      item.onClick()
      onClose()
    }
  }, [onClose])

  // Adjust position to keep menu on screen
  const adjustedPosition = {
    x: Math.min(position.x, window.innerWidth - 220),
    y: Math.min(position.y, window.innerHeight - items.length * 40)
  }

  return (
    <div
      className="context-menu"
      style={{
        position: 'fixed',
        left: adjustedPosition.x,
        top: adjustedPosition.y,
        zIndex: 10000
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {items.map((item, index) => (
        item.divider ? (
          <div key={`divider-${index}`} className="context-menu-divider" />
        ) : (
          <button
            key={index}
            className={`context-menu-item ${item.disabled ? 'disabled' : ''} ${item.destructive ? 'destructive' : ''}`}
            onClick={() => handleItemClick(item)}
            disabled={item.disabled}
          >
            {item.icon && <span className="context-menu-icon">{item.icon}</span>}
            <span className="context-menu-label">{item.label}</span>
            {item.shortcut && <span className="context-menu-shortcut">{item.shortcut}</span>}
          </button>
        )
      ))}
    </div>
  )
}

export default memo(ContextMenu)

/**
 * Helper function to build context menu items based on selection
 */
export function buildContextMenuItems(
  selectedNodes: Node[],
  handlers: {
    onGroup: () => void
    onUngroup: () => void
    onLock: () => void
    onUnlock: () => void
    onDelete: () => void
    onDuplicate: () => void
    onPaste?: () => void
    canPaste?: boolean
    onToggleAutoResize?: () => void
    onAlign?: (edge: 'left' | 'right' | 'hcenter' | 'top' | 'bottom' | 'vcenter') => void
    onDistribute?: (axis: 'horizontal' | 'vertical') => void
    onTidyUp?: () => void
    onBringToFront?: () => void
    onBringForward?: () => void
    onSendBackward?: () => void
    onSendToBack?: () => void
  }
): ContextMenuItem[] {
  const items: ContextMenuItem[] = []

  /**
   * Append z-order (front/back/forward/backward) items. Shared between the
   * single- and multi-select branches; added whenever ≥1 node is selected and
   * the handlers are provided.
   */
  const pushZOrderItems = () => {
    if (
      !handlers.onBringToFront &&
      !handlers.onBringForward &&
      !handlers.onSendBackward &&
      !handlers.onSendToBack
    ) {
      return
    }
    items.push({ label: '', onClick: () => {}, divider: true })
    if (handlers.onBringToFront) {
      items.push({
        label: 'Bring to Front',
        icon: <BringToFront size={16} />,
        onClick: handlers.onBringToFront,
        shortcut: 'Ctrl+Shift+]',
      })
    }
    if (handlers.onBringForward) {
      items.push({
        label: 'Bring Forward',
        icon: <ArrowUp size={16} />,
        onClick: handlers.onBringForward,
        shortcut: 'Ctrl+]',
      })
    }
    if (handlers.onSendBackward) {
      items.push({
        label: 'Send Backward',
        icon: <ArrowDown size={16} />,
        onClick: handlers.onSendBackward,
        shortcut: 'Ctrl+[',
      })
    }
    if (handlers.onSendToBack) {
      items.push({
        label: 'Send to Back',
        icon: <SendToBack size={16} />,
        onClick: handlers.onSendToBack,
        shortcut: 'Ctrl+Shift+[',
      })
    }
  }

  // No selection - show paste if available
  if (selectedNodes.length === 0) {
    items.push({
      label: 'Paste',
      icon: <Clipboard size={16} />,
      onClick: handlers.onPaste ?? (() => {}),
      disabled: !handlers.canPaste,
      shortcut: 'Ctrl+V'
    })
    return items
  }

  // Single node selection
  if (selectedNodes.length === 1) {
    const node = selectedNodes[0]
    const isContainer = node.type === 'group-container' || node.type === 'container'
    const isLocked = node.data?.locked === true

    // Container-specific options
    if (isContainer) {
      items.push({
        label: 'Ungroup',
        icon: <Ungroup size={16} />,
        onClick: handlers.onUngroup
      })

      // Auto-resize toggle for containers
      if (handlers.onToggleAutoResize) {
        const autoResize = node.data?.autoResize === true
        items.push({
          label: autoResize ? 'Disable Auto-resize' : 'Enable Auto-resize',
          icon: autoResize ? <Minimize2 size={16} /> : <Maximize2 size={16} />,
          onClick: handlers.onToggleAutoResize
        })
      }

      items.push({ label: '', onClick: () => {}, divider: true })
    }

    // Lock/Unlock
    items.push({
      label: isLocked ? 'Unlock' : 'Lock',
      icon: isLocked ? <Unlock size={16} /> : <Lock size={16} />,
      onClick: isLocked ? handlers.onUnlock : handlers.onLock
    })

    items.push({ label: '', onClick: () => {}, divider: true })

    // Duplicate
    items.push({
      label: 'Duplicate',
      icon: <Copy size={16} />,
      onClick: handlers.onDuplicate,
      shortcut: 'Ctrl+D'
    })

    // Delete
    items.push({
      label: 'Delete',
      icon: <Trash2 size={16} />,
      onClick: handlers.onDelete,
      destructive: true,
      shortcut: 'Del'
    })

    // Z-order controls (front/back/forward/backward)
    pushZOrderItems()
  }

  // Multiple nodes selection
  if (selectedNodes.length > 1) {
    const allLocked = selectedNodes.every(n => n.data?.locked === true)
    const anyLocked = selectedNodes.some(n => n.data?.locked === true)

    // Group selection
    items.push({
      label: 'Group Selection',
      icon: <Group size={16} />,
      onClick: handlers.onGroup,
      shortcut: 'Ctrl+G'
    })

    // Align / Distribute / Tidy up (only when handlers provided)
    const arrangeItems: ContextMenuItem[] = []
    if (handlers.onAlign) {
      arrangeItems.push({
        label: 'Align left',
        icon: <AlignHorizontalJustifyStart size={16} />,
        onClick: () => handlers.onAlign!('left')
      })
      arrangeItems.push({
        label: 'Align top',
        icon: <AlignVerticalJustifyStart size={16} />,
        onClick: () => handlers.onAlign!('top')
      })
    }
    if (handlers.onDistribute && selectedNodes.length >= 3) {
      arrangeItems.push({
        label: 'Distribute horizontally',
        icon: <AlignHorizontalDistributeCenter size={16} />,
        onClick: () => handlers.onDistribute!('horizontal')
      })
    }
    if (handlers.onTidyUp) {
      arrangeItems.push({
        label: 'Tidy up',
        icon: <LayoutGrid size={16} />,
        onClick: handlers.onTidyUp
      })
    }
    if (arrangeItems.length > 0) {
      items.push({ label: '', onClick: () => {}, divider: true })
      items.push(...arrangeItems)
    }

    items.push({ label: '', onClick: () => {}, divider: true })

    // Lock/Unlock all
    if (allLocked) {
      items.push({
        label: 'Unlock All',
        icon: <Unlock size={16} />,
        onClick: handlers.onUnlock
      })
    } else if (anyLocked) {
      items.push({
        label: 'Lock All',
        icon: <Lock size={16} />,
        onClick: handlers.onLock
      })
    } else {
      items.push({
        label: 'Lock All',
        icon: <Lock size={16} />,
        onClick: handlers.onLock
      })
    }

    items.push({ label: '', onClick: () => {}, divider: true })

    // Delete all
    items.push({
      label: `Delete ${selectedNodes.length} items`,
      icon: <Trash2 size={16} />,
      onClick: handlers.onDelete,
      destructive: true,
      shortcut: 'Del'
    })

    // Z-order controls (front/back/forward/backward)
    pushZOrderItems()
  }

  return items
}
